import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { ordersAPI, extractErrorMessage } from '../api';

const Orders = () => {
  const { selectedStore } = useStore();
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [editingSupplierId, setEditingSupplierId] = useState(null);
  const [showReceiveModal, setShowReceiveModal] = useState(null);
  const [supplierForm, setSupplierForm] = useState({
    name: '', contact_name: '', email: '', phone: '', address: '', notes: '',
  });

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ordersAPI.list(selectedStore.id);
      setOrders(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load orders'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  const loadSuppliers = useCallback(async () => {
    try {
      const response = await ordersAPI.listSuppliers(selectedStore.id);
      setSuppliers(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) { /* optional */ }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStore) {
      loadOrders();
      loadSuppliers();
    }
  }, [selectedStore, loadOrders, loadSuppliers]);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      await ordersAPI.generate(selectedStore.id);
      await loadOrders();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to generate orders'));
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await ordersAPI.approve(id);
      loadOrders();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to approve order'));
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await ordersAPI.update(id, { status });
      loadOrders();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to update order'));
    }
  };

  const handleReceiveOrder = async () => {
    if (!showReceiveModal) return;
    try {
      await ordersAPI.update(showReceiveModal.id, { status: 'received' });
      loadOrders();
      setShowReceiveModal(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to receive order'));
    }
  };

  const handleSupplierSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...supplierForm, store_id: selectedStore.id };
      if (editingSupplierId) {
        await ordersAPI.updateSupplier(editingSupplierId, data);
      } else {
        await ordersAPI.createSupplier(data);
      }
      loadSuppliers();
      resetSupplierForm();
    } catch (err) {
      setError(extractErrorMessage(err, 'Operation failed'));
    }
  };

  const handleEditSupplier = (supplier) => {
    setSupplierForm({
      name: supplier.name || '', contact_name: supplier.contact_name || '',
      email: supplier.email || '', phone: supplier.phone || '',
      address: supplier.address || '', notes: supplier.notes || '',
    });
    setEditingSupplierId(supplier.id);
    setShowSupplierForm(true);
  };

  const handleDeleteSupplier = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    try {
      await ordersAPI.deleteSupplier(id);
      loadSuppliers();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to delete supplier'));
    }
  };

  const resetSupplierForm = () => {
    setSupplierForm({ name: '', contact_name: '', email: '', phone: '', address: '', notes: '' });
    setEditingSupplierId(null);
    setShowSupplierForm(false);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'draft': return 'badge-info';
      case 'pending': return 'badge-warning';
      case 'approved': return 'badge-info';
      case 'ordered': return 'badge-info';
      case 'shipped': return 'badge-info';
      case 'received': return 'badge-success';
      case 'cancelled': return 'badge-danger';
      default: return '';
    }
  };

  if (loading) return <div className="loading"><div className="spinner"></div><span>Loading orders...</span></div>;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Supplier Orders</h1>
        <div className="flex gap-2">
          {activeTab === 'orders' && (
            <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}>
              {generating ? 'Generating...' : 'Auto-Generate Orders'}
            </button>
          )}
          {activeTab === 'suppliers' && (
            <button className="btn btn-primary" onClick={() => { setShowSupplierForm(!showSupplierForm); if (showSupplierForm) resetSupplierForm(); }}>
              {showSupplierForm ? 'Cancel' : '+ Add Supplier'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="tab-bar">
        <button className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>Orders</button>
        <button className={`tab-btn ${activeTab === 'suppliers' ? 'active' : ''}`} onClick={() => setActiveTab('suppliers')}>Suppliers</button>
      </div>

      {/* Receive Stock Modal */}
      {showReceiveModal && (
        <div className="modal-overlay" onClick={() => setShowReceiveModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Receive Stock</h3>
              <button className="modal-close" onClick={() => setShowReceiveModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem' }}>Mark order <strong>#{showReceiveModal.order_number}</strong> as received? This will update inventory.</p>
              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleReceiveOrder}>Confirm Receipt</button>
                <button className="btn btn-secondary" onClick={() => setShowReceiveModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <>
          {/* Mobile Cards */}
          <div className="mobile-cards hidden-desktop">
            {orders.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <h3 className="empty-state-title">No orders yet</h3>
              </div>
            ) : (
              orders.map(order => (
                <div className="mobile-card" key={order.id}>
                  <div className="mobile-card-header">
                    <div>
                      <div className="mobile-card-title">{order.order_number}</div>
                      <div className="text-muted" style={{ fontSize: '0.8rem' }}>{new Date(order.created_at).toLocaleDateString()}</div>
                    </div>
                    <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                      {order.status}{order.auto_generated ? ' (Auto)' : ''}
                    </span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Items</span>
                    <span>{order.order_items?.length || 0} items</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Total</span>
                    <span className="font-bold">${parseFloat(order.total_amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="mobile-card-actions">
                    {(order.status === 'pending' || order.status === 'draft') && (
                      <button className="btn btn-sm btn-success" onClick={() => handleApprove(order.id)}>Approve</button>
                    )}
                    {order.status === 'approved' && (
                      <button className="btn btn-sm btn-primary" onClick={() => handleUpdateStatus(order.id, 'ordered')}>Mark Ordered</button>
                    )}
                    {order.status === 'ordered' && (
                      <button className="btn btn-sm btn-primary" onClick={() => handleUpdateStatus(order.id, 'shipped')}>Mark Shipped</button>
                    )}
                    {order.status === 'shipped' && (
                      <button className="btn btn-sm btn-success" onClick={() => setShowReceiveModal(order)}>Receive Stock</button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Desktop Table */}
          <div className="card desktop-table">
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Order #</th><th>Date</th><th>Items</th><th>Total</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => (
                    <tr key={order.id}>
                      <td className="font-medium">{order.order_number}</td>
                      <td>{new Date(order.created_at).toLocaleDateString()}</td>
                      <td>{order.order_items?.length || 0} items</td>
                      <td className="font-bold">${parseFloat(order.total_amount || 0).toFixed(2)}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(order.status)}`}>{order.status}</span>
                        {order.auto_generated && <small className="text-muted" style={{ marginLeft: '5px' }}>(Auto)</small>}
                      </td>
                      <td>
                        <div className="flex gap-2">
                          {(order.status === 'pending' || order.status === 'draft') && (
                            <button className="btn btn-sm btn-success" onClick={() => handleApprove(order.id)}>Approve</button>
                          )}
                          {order.status === 'approved' && (
                            <button className="btn btn-sm btn-primary" onClick={() => handleUpdateStatus(order.id, 'ordered')}>Mark Ordered</button>
                          )}
                          {order.status === 'ordered' && (
                            <button className="btn btn-sm btn-primary" onClick={() => handleUpdateStatus(order.id, 'shipped')}>Mark Shipped</button>
                          )}
                          {order.status === 'shipped' && (
                            <button className="btn btn-sm btn-success" onClick={() => setShowReceiveModal(order)}>Receive Stock</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {orders.length === 0 && <tr><td colSpan="6" className="text-center text-muted" style={{ padding: '3rem' }}>No orders found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Suppliers Tab */}
      {activeTab === 'suppliers' && (
        <>
          {showSupplierForm && (
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header">
                <h3 className="card-title">{editingSupplierId ? 'Edit Supplier' : 'Add Supplier'}</h3>
              </div>
              <div className="card-body">
                <form onSubmit={handleSupplierSubmit}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Company Name *</label>
                      <input type="text" className="form-input" value={supplierForm.name}
                        onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Contact Name</label>
                      <input type="text" className="form-input" value={supplierForm.contact_name}
                        onChange={(e) => setSupplierForm({ ...supplierForm, contact_name: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input type="email" className="form-input" value={supplierForm.email}
                        onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input type="tel" className="form-input" value={supplierForm.phone}
                        onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Address</label>
                    <textarea className="form-textarea" value={supplierForm.address} rows={2}
                      onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <textarea className="form-textarea" value={supplierForm.notes} rows={2}
                      onChange={(e) => setSupplierForm({ ...supplierForm, notes: e.target.value })} />
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">{editingSupplierId ? 'Update' : 'Create Supplier'}</button>
                    {editingSupplierId && <button type="button" className="btn btn-secondary" onClick={resetSupplierForm}>Cancel</button>}
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Mobile Cards */}
          <div className="mobile-cards hidden-desktop">
            {suppliers.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🏭</div>
                <h3 className="empty-state-title">No suppliers yet</h3>
              </div>
            ) : (
              suppliers.map(supplier => (
                <div className="mobile-card" key={supplier.id}>
                  <div className="mobile-card-header">
                    <div className="mobile-card-title">{supplier.name}</div>
                  </div>
                  {supplier.contact_name && <div className="mobile-card-row"><span className="mobile-card-label">Contact</span><span>{supplier.contact_name}</span></div>}
                  {supplier.email && <div className="mobile-card-row"><span className="mobile-card-label">Email</span><span>{supplier.email}</span></div>}
                  {supplier.phone && <div className="mobile-card-row"><span className="mobile-card-label">Phone</span><span>{supplier.phone}</span></div>}
                  <div className="mobile-card-actions">
                    <button className="btn btn-sm btn-secondary" onClick={() => handleEditSupplier(supplier)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteSupplier(supplier.id)}>Delete</button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Desktop Table */}
          <div className="card desktop-table">
            <div className="table-container">
              <table>
                <thead>
                  <tr><th>Name</th><th>Contact</th><th>Email</th><th>Phone</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {suppliers.map(supplier => (
                    <tr key={supplier.id}>
                      <td className="font-medium">{supplier.name}</td>
                      <td>{supplier.contact_name || '-'}</td>
                      <td className="text-muted">{supplier.email || '-'}</td>
                      <td>{supplier.phone || '-'}</td>
                      <td>
                        <div className="flex gap-2">
                          <button className="btn btn-sm btn-secondary" onClick={() => handleEditSupplier(supplier)}>Edit</button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDeleteSupplier(supplier.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {suppliers.length === 0 && <tr><td colSpan="5" className="text-center text-muted" style={{ padding: '3rem' }}>No suppliers found.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Orders;
